<script lang="ts">
  import { debounce } from "lodash";
  import { createEventDispatcher } from "svelte";
  import { api } from "../api/client";
  import type { Recipe } from "../helpers/types";
  import ButtonSmall from "./ButtonSmall.svelte";
  import LoadingSpinner from "./LoadingSpinner.svelte";
  import QuantitySlider from "./QuantitySlider.svelte";

  export let buyer: any;
  export let recipe: Recipe;
  export const index: number = -1;
  export let dirty: boolean = false;
  export let saving: boolean = false;
  export let onSearch: (query: string, recipeItems: string[]) => Promise<Partial<Recipe[]>>;
  export let fresh: boolean = false;
  export let percentLinked: number | null = null;

  let expandable: boolean = false;
  let open: boolean = fresh || false;
  let searchResults: Partial<Recipe[]> = [];
  let searchQuery: string = "";

  let editName: boolean = fresh ? true : false;
  let recipeNameInput: HTMLInputElement | null = null;

  const dispatch = createEventDispatcher();

  const updateRecipe = (recipe: Recipe) => {
    dispatch("update", { recipe });
  };

  const saveRecipe = (recipe: Recipe) => {
    dispatch("save", { recipe });
  };

  const deleteRecipe = (recipe: Recipe) => {
    dispatch("delete", { recipe });
  };

  const linkRecipe = (recipe: Recipe) => {
    dispatch("link", { recipe });
  };

  const debouncedSearch = debounce(async (e) => {
    const searchQuery = e.target.value;
    if (searchQuery.length === 0) {
      searchResults = [];
      return;
    }

    const results = await onSearch(
      searchQuery,
      recipe.items?.flatMap((i) => i.id ?? [])
    );
    searchResults = results;
  }, 300);

  const fetchRecipeDetails = async (recipe_id: string) => {
    try {
      const body = {
        buyer_id: buyer?.id,
      };
      const result = await api
        .post(`recipe/${recipe_id}`, { body: JSON.stringify(body) })
        .json<any>();
      let recipe = result.recipe;
      recipe.type = "recipe";
      return recipe;
    } catch (e) {
      console.error(e);
    }
  };

  function isResultRecipe(result: Partial<Recipe> | Recipe): result is Recipe {
    return result.type == "recipe";
  }

  const handleClick = async (result: Partial<Recipe>) => {
    if (isResultRecipe(result) && result.id) {
      result = await fetchRecipeDetails(result.id);
    }
    if (recipe.items === undefined) recipe.items = [];
    recipe.items = [...recipe.items, { ...result, usage: 2 } as Recipe];
    updateRecipe(recipe);
    searchQuery = "";
    searchResults = [];
  };

  function addIngredient() {
    const newItem = {
      name: searchQuery,
      usage: 2,
      type: "ingredient",
    } as Recipe;
    if (recipe.items === undefined) recipe.items = [];
    recipe.items = [...recipe.items, newItem];
    updateRecipe(recipe);
    searchQuery = "";
  }

  function addSubRecipe() {
    const newItem = {
      name: searchQuery,
      usage: 2,
      type: "recipe",
    } as Recipe;
    if (recipe.items === undefined) recipe.items = [];
    recipe.items = [...recipe.items, newItem];
    updateRecipe(recipe);
    searchQuery = "";
  }

  function resizeInputOnDynamicContent(node: HTMLInputElement) {
    const measuringElement = document.createElement("div");
    document.body.appendChild(measuringElement);

    function duplicateAndSet() {
      const styles = window.getComputedStyle(node);
      measuringElement.innerHTML = node.value ? node.value : node.placeholder;
      measuringElement.style.fontSize = styles.fontSize;
      measuringElement.style.fontFamily = styles.fontFamily;
      measuringElement.style.paddingLeft = styles.paddingLeft;
      measuringElement.style.paddingRight = styles.paddingRight;
      measuringElement.style.boxSizing = "border-box";
      measuringElement.style.border = styles.border;
      measuringElement.style.width = "max-content";
      measuringElement.style.position = "absolute";
      measuringElement.style.top = "0";
      measuringElement.style.left = "-9999px";
      measuringElement.style.overflow = "hidden";
      measuringElement.style.visibility = "hidden";
      measuringElement.style.whiteSpace = "pre";
      measuringElement.style.height = "0";
      node.style.width = `${measuringElement.offsetWidth}px`;
    }

    duplicateAndSet();
    const observer = new MutationObserver(duplicateAndSet);
    observer.observe(node, { attributes: true, childList: true, subtree: true });

    node.addEventListener("input", duplicateAndSet);
    return {
      destroy() {
        observer.disconnect();
        node.removeEventListener("input", duplicateAndSet);
        measuringElement.remove();
      },
    };
  }

  $: expandable = recipe.type == "recipe";
  $: fresh = recipe.id === undefined;
  $: collapseIcon = open ? "-" : expandable ? "+" : "\u00A0";
</script>

<div
  class="recipe"
  class:open
  class:dirty
  class:inside={recipe.type == "ingredient" || recipe.is_sub_recipe}
>
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="recipe-header {expandable ? 'cursor-oversized' : ''}"
    on:click={() => {
      if (!expandable) return;
      open = !open;
    }}
  >
    <div class="collapse-handler">
      <div class="colapse-icon">{collapseIcon}</div>
    </div>
    <div class="recipe-name">
      {#if editName}
        <input
          class="recipe-name"
          autocomplete="off"
          type="text"
          bind:value={recipe.name}
          bind:this={recipeNameInput}
          placeholder="new recipe name"
          on:input={() => {
            dispatch("update", { recipe });
          }}
          on:blur={() => {
            editName = false;
          }}
          on:click={(e) => {
            e.stopPropagation();
          }}
        />
      {:else}
        {recipe.name}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class="edit"
          on:click={(e) => {
            e.stopPropagation();
            editName = true;
            setTimeout(() => {
              recipeNameInput?.focus();
            }, 10);
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
              id="SVGRepo_tracerCarrier"
              stroke-linecap="round"
              stroke-linejoin="round"
            /><g id="SVGRepo_iconCarrier"
              ><path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="m3.99 16.854-1.314 3.504a.75.75 0 0 0 .966.965l3.503-1.314a3 3 0 0 0 1.068-.687L18.36 9.175s-.354-1.061-1.414-2.122c-1.06-1.06-2.122-1.414-2.122-1.414L4.677 15.786a3 3 0 0 0-.687 1.068zm12.249-12.63 1.383-1.383c.248-.248.579-.406.925-.348.487.08 1.232.322 1.934 1.025.703.703.945 1.447 1.025 1.934.058.346-.1.677-.348.925L19.774 7.76s-.353-1.06-1.414-2.12c-1.06-1.062-2.121-1.415-2.121-1.415z"
                fill="#000000"
              /></g
            ></svg
          >
        </div>
      {/if}
    </div>
    {#if recipe.usage}
      <QuantitySlider
        value={recipe.usage}
        on:change={(e) => {
          if (e.detail.value === recipe.usage) return;
          recipe.usage = e.detail.value;
          updateRecipe(recipe);
        }}
      />
      <ButtonSmall color="light" disabled={saving} size="tiny" on:click={() => dispatch("remove")}>
        {#if saving}
          <LoadingSpinner size={20} />
        {:else}
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
              id="SVGRepo_tracerCarrier"
              stroke-linecap="round"
              stroke-linejoin="round"
            /><g id="SVGRepo_iconCarrier">
              <path
                d="M4 6H20M16 6L15.7294 5.18807C15.4671 4.40125 15.3359 4.00784 15.0927 3.71698C14.8779 3.46013 14.6021 3.26132 14.2905 3.13878C13.9376 3 13.523 3 12.6936 3H11.3064C10.477 3 10.0624 3 9.70951 3.13878C9.39792 3.26132 9.12208 3.46013 8.90729 3.71698C8.66405 4.00784 8.53292 4.40125 8.27064 5.18807L8 6M18 6V16.2C18 17.8802 18 18.7202 17.673 19.362C17.3854 19.9265 16.9265 20.3854 16.362 20.673C15.7202 21 14.8802 21 13.2 21H10.8C9.11984 21 8.27976 21 7.63803 20.673C7.07354 20.3854 6.6146 19.9265 6.32698 19.362C6 18.7202 6 17.8802 6 16.2V6M14 10V17M10 10V17"
                stroke="#000000"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
          </svg>
        {/if}
      </ButtonSmall>
    {:else}
      <div class="actions" class:saving>
        {#if !(fresh || dirty) && percentLinked !== null}
          <ButtonSmall
            color="light"
            disabled={saving}
            size="tiny"
            on:click={(e) => {
              e.stopPropagation();
              linkRecipe(recipe);
            }}
          >
            {#if percentLinked < 1}
              <div class="warning">
                <svg
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                    id="SVGRepo_tracerCarrier"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  /><g id="SVGRepo_iconCarrier">
                    <path
                      d="M12 5L12 14"
                      stroke="#000000"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13.8417 18.8417C13.8417 19.8588 13.0171 20.6833 12 20.6833C10.9829 20.6833 10.1583 19.8588 10.1583 18.8417C10.1583 17.8245 10.9829 17 12 17C13.0171 17 13.8417 17.8245 13.8417 18.8417Z"
                      fill="#000000"
                    />
                  </g></svg
                >
              </div>
              MISSING LINKS
            {:else}
              MANAGE LINKS
            {/if}
          </ButtonSmall>
        {/if}
        {#if recipe.id}
          <ButtonSmall
            color="light"
            disabled={saving}
            size="tiny"
            on:click={(e) => {
              e.stopPropagation();
              deleteRecipe(recipe);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
              /><g id="SVGRepo_iconCarrier">
                <path
                  d="M4 6H20M16 6L15.7294 5.18807C15.4671 4.40125 15.3359 4.00784 15.0927 3.71698C14.8779 3.46013 14.6021 3.26132 14.2905 3.13878C13.9376 3 13.523 3 12.6936 3H11.3064C10.477 3 10.0624 3 9.70951 3.13878C9.39792 3.26132 9.12208 3.46013 8.90729 3.71698C8.66405 4.00784 8.53292 4.40125 8.27064 5.18807L8 6M18 6V16.2C18 17.8802 18 18.7202 17.673 19.362C17.3854 19.9265 16.9265 20.3854 16.362 20.673C15.7202 21 14.8802 21 13.2 21H10.8C9.11984 21 8.27976 21 7.63803 20.673C7.07354 20.3854 6.6146 19.9265 6.32698 19.362C6 18.7202 6 17.8802 6 16.2V6M14 10V17M10 10V17"
                  stroke="#000000"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g></svg
            >
          </ButtonSmall>
        {/if}
        {#if dirty}
          <ButtonSmall
            disabled={saving}
            size="tiny"
            on:click={(e) => {
              e.stopPropagation();
              saveRecipe(recipe);
            }}
          >
            {#if saving}
              <LoadingSpinner size={20} />
            {:else}
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                style="filter:invert(1)"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                  id="SVGRepo_tracerCarrier"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                /><g id="SVGRepo_iconCarrier">
                  <path
                    d="M4 12.6111L8.92308 17.5L20 6.5"
                    stroke="#000000"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </g></svg
              >
            {/if}
          </ButtonSmall>
        {/if}
      </div>
    {/if}
  </div>
  {#if (recipe.type == "recipe" || fresh) && open}
    <div class="recipe-content">
      {#if recipe.items}
        {#each recipe.items as item, index}
          <svelte:self
            recipe={item}
            {buyer}
            {index}
            fresh={!item.id && item.type == "recipe"}
            {onSearch}
            on:update={(e) => {
              recipe.items[index] = e.detail.recipe;
              updateRecipe(recipe);
            }}
            on:remove={() => {
              recipe.items.splice(index, 1);
              updateRecipe(recipe);
            }}
          />
        {/each}
      {/if}
      <div class="recipe new-item recipe-header">
        <div class="collapse-handler">
          <svg
            viewBox="0 0 20 20"
            width="16"
            height="16"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
              id="SVGRepo_tracerCarrier"
              stroke-linecap="round"
              stroke-linejoin="round"
            /><g id="SVGRepo_iconCarrier">
              <path
                fill="#000000"
                fill-rule="evenodd"
                d="M4 9a5 5 0 1110 0A5 5 0 014 9zm5-7a7 7 0 104.2 12.6.999.999 0 00.093.107l3 3a1 1 0 001.414-1.414l-3-3a.999.999 0 00-.107-.093A7 7 0 009 2z"
              />
            </g>
          </svg>
        </div>
        <div class="form-control-inline">
          <input
            autocomplete="off"
            class="recipe-name-search"
            class:options-visible={searchResults.length}
            id="input"
            bind:value={searchQuery}
            use:resizeInputOnDynamicContent
            on:input={debouncedSearch}
            placeholder="start typing name of ingredient or recipe"
            on:keydown={(e) => {
              if (e.key === "Enter") {
                addIngredient();
                searchResults = [];
              }
            }}
          />

          {#if searchResults.length > 0}
            <div class="options-container">
              {#each searchResults as result (result)}
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <div
                  class="option"
                  on:click={() => {
                    if (!result) return;
                    handleClick(result);
                  }}
                >
                  {#if result?.type == "recipe"}
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      /><g id="SVGRepo_iconCarrier">
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M7.25285 4.25547C8.09403 2.47951 9.90263 1.25 12 1.25C14.0974 1.25 15.906 2.47951 16.7471 4.25547C16.831 4.25184 16.9153 4.25 17 4.25C20.1756 4.25 22.75 6.82436 22.75 10C22.75 12.1806 21.5363 14.0762 19.75 15.0508L19.75 18.052C19.75 18.9505 19.7501 19.6997 19.6701 20.2945C19.5857 20.9223 19.4 21.4891 18.9445 21.9445C18.4891 22.4 17.9223 22.5857 17.2945 22.6701C16.6997 22.7501 15.9505 22.75 15.052 22.75H8.94801C8.04952 22.75 7.3003 22.7501 6.70552 22.6701C6.07773 22.5857 5.51093 22.4 5.05546 21.9445C4.59999 21.4891 4.41432 20.9223 4.32991 20.2945C4.24994 19.6997 4.24997 18.9505 4.25 18.052L4.25 15.0508C2.46371 14.0762 1.25 12.1806 1.25 10C1.25 6.82436 3.82436 4.25 7 4.25C7.08469 4.25 7.16899 4.25184 7.25285 4.25547ZM6.80262 5.7545C4.54704 5.85762 2.75 7.71895 2.75 10C2.75 11.7416 3.79769 13.2402 5.30028 13.8967C5.57345 14.016 5.75 14.2859 5.75 14.584V17.25H18.25L18.25 14.584C18.25 14.2859 18.4265 14.016 18.6997 13.8967C20.2023 13.2402 21.25 11.7416 21.25 10C21.25 7.71895 19.453 5.85761 17.1974 5.7545C17.2321 5.99825 17.25 6.24718 17.25 6.5V7C17.25 7.41421 16.9142 7.75 16.5 7.75C16.0858 7.75 15.75 7.41421 15.75 7V6.5C15.75 6.07715 15.6803 5.67212 15.5524 5.29486C15.0502 3.81402 13.6484 2.75 12 2.75C10.3516 2.75 8.94981 3.81402 8.44763 5.29486C8.3197 5.67212 8.25 6.07715 8.25 6.5V7C8.25 7.41421 7.91421 7.75 7.5 7.75C7.08579 7.75 6.75 7.41421 6.75 7V6.5C6.75 6.24717 6.76792 5.99825 6.80262 5.7545ZM18.2482 18.75H5.75181C5.75604 19.3194 5.77008 19.7491 5.81654 20.0946C5.87858 20.5561 5.9858 20.7536 6.11612 20.8839C6.24643 21.0142 6.44393 21.1214 6.90539 21.1835C7.38843 21.2484 8.03599 21.25 9 21.25H15C15.964 21.25 16.6116 21.2484 17.0946 21.1835C17.5561 21.1214 17.7536 21.0142 17.8839 20.8839C18.0142 20.7536 18.1214 20.5561 18.1835 20.0946C18.2299 19.7491 18.244 19.3194 18.2482 18.75Z"
                          fill="#1C274C"
                        />
                      </g></svg
                    >
                  {:else if result?.type == "ingredient"}
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                        id="SVGRepo_tracerCarrier"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      /><g id="SVGRepo_iconCarrier">
                        <path
                          d="M4.44893 17.009C-0.246384 7.83762 7.34051 0.686125 19.5546 3.61245C20.416 3.81881 21.0081 4.60984 20.965 5.49452C20.5862 13.288 17.0341 17.7048 6.13252 17.9857C5.43022 18.0038 4.76908 17.6344 4.44893 17.009Z"
                          stroke="#323232"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M3.99999 21C5.50005 15.5 6 12.5 12 9.99997"
                          stroke="#323232"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </g></svg
                    >
                  {/if}
                  {result?.name}
                </div>
              {/each}
            </div>
          {/if}
        </div>

        <ButtonSmall size="tiny" on:click={addIngredient}>add ingredient</ButtonSmall>
        <ButtonSmall size="tiny" on:click={addSubRecipe} color="neutral-01"
          >add sub recipe</ButtonSmall
        >
      </div>
    </div>
  {/if}
</div>

<style>
  .recipe {
    display: flex;
    flex-direction: column;
    border-radius: 14px;
    background-color: var(--rekki-neutral-01);
    font-size: 16px;
  }
  .recipe.inside {
    background-color: var(--rekki-neutral-00);
  }
  .recipe-header {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    padding: 0 20px 0 0;
  }
  .recipe-name {
    flex: 1;
    background-color: transparent;
    border: none;
    font-size: 16px;
    display: flex;
    flex-direction: row;
    gap: 10px;
  }
  .recipe-name-search {
    flex: 1;
    background-color: transparent;
    border: none;
    font-size: 16px;
    display: flex;
    flex-direction: row;
    gap: 10px;
  }
  .recipe.new-item {
    display: flex;
    gap: 10px;
    flex-direction: row;
    background-color: var(--rekki-neutral-00);
  }
  .form-control {
    flex: 1;
    display: flex;
    position: relative;
  }

  .form-control-inline {
    position: relative;
  }

  .options-container {
    position: absolute;
    max-height: 150px;
    overflow-y: auto;
    top: 100%;
    padding: 6px 0;
    background-color: #fff;
    border-radius: 6px;
    box-shadow: 4px 4px 9px rgba(0, 0, 0, 0.1);
    z-index: 1;
  }

  .options-container .option {
    padding: 2px 10px;
    cursor: pointer;
    white-space: nowrap;
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .options-container .option:hover {
    color: #fff;
    background-color: #000;
  }

  .option:hover svg {
    filter: invert(1);
  }

  .collapse-handler {
    display: flex;
    align-items: center;
    padding: 14px 20px;
  }
  .actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .actions.saving {
    opacity: 0.5;
    pointer-events: none;
  }
  .recipe-content {
    background-color: var(--rekki-neutral-02);
    background-color: #fff;
    border-radius: 0 0 14px 14px;
    padding: 10px;
    gap: 10px;
    display: flex;
    flex-direction: column;
    border: 6px solid var(--rekki-neutral-01);
  }
  .new-item {
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .warning {
    font-size: 12px;
    font-weight: bold;
    background-color: var(--rekki-warning);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }
  input {
    font: inherit;
    letter-spacing: inherit;
  }
  .colapse-icon {
    width: 20px;
    text-align: center;
  }
  .edit {
    opacity: 0.3;
  }
  .edit:hover {
    opacity: 1;
  }
</style>
